<script setup lang="ts">
import SelectionItemEmptyState from '@organisms/offer/selection-items/components/SelectionItemEmptyState.vue';
import type { User as AuthOrganizationUser } from '@models/auth/organization/user';
import type { User as AuthUser } from '@models/auth/user';
import type { Organization as AuthOrganization } from '@models/auth/organization';
import { storeToRefs } from 'pinia';
import Sortable from 'sortablejs';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import { Direction } from '@enums/direction';
import { SelectionItemProductTour } from '@enums/productTour';
import { type SelectionItem } from '@models/selectionItem.ts';
import type { LinkTarget } from '@global-types/linkTarget';
import { type Url } from '@global-types/url';
import Button from '@components/Button.vue';
import ButtonLink from '@components/ButtonLink.vue';
import CounterPill from '@components/CounterPill.vue';
import Icon from '@components/Icon.vue';
import Tooltip from '@components/Tooltip.vue';
import Typography from '@components/Typography.vue';
import UserPicture from '@components/UserPicture.vue';
import TooltipClick from '@components/TooltipClick.vue';
import { ViewType } from '@components-molecules/map/map-constants';
import { useScreensizeStore } from '@stores/useScreensizeStore';
import { URL_GUIDED_SEARCH, URL_SEARCH, URL_USER_ORGANIZATION_TEAM } from '@tools/routes/routes';
import { UserAuth } from '@tools/user/auth';
import { trans, transchoice } from '@elements/translation';
import IconUploadSimple from '@icons/ph_upload-simple.svg';
import IconWhatsapp from '@icons/ph_whatsapp.svg';
import IconEnvelope from '@icons/ph_envelope.svg';
import IconUserPlus from '@icons/ph_user-plus.svg';
import StickyTabs from '@components-molecules/map/StickyTabs.vue';
import SelectionItemMap from '@organisms/offer/selection-items/components/map/SelectionItemMap.vue';
import CustomMarkerSlider from './components/map/CustomMarkerSlider.vue';
import CommentsSlider from './components/comments/CommentsSlider.vue';
import AccountManagerContactPanel from './components/AccountManagerContactPanel.vue';
import SelectionItemElement from './components/SelectionItemElement.vue';
import SelectionItemWrapper from './components/SelectionItemWrapper.vue';
import { useSelectionItemCommentsStore } from './stores/useSelectionItemCommentsStore.ts';
import { useSelectionItemStore } from './stores/useSelectionItemStore';

const selectionItemStore = useSelectionItemStore();

const {
  activeSelectionItems,
  removedSelectionItems,
  currentView,
  accountManager,
} = storeToRefs(selectionItemStore);

const {
  setView,
  reorderItems,
  sendEmailWithLastProposal,
} = selectionItemStore;

const { openCommentsModal } = useSelectionItemCommentsStore();

const sortableSelectionItems = useTemplateRef<HTMLUListElement | null>('sortableSelectionItems');

const teamLink = ref<Url>(URL_USER_ORGANIZATION_TEAM);
const currentUser = computed<AuthUser>(() => UserAuth.getUser()!);
const currentOrganization = computed<AuthOrganization>(() => currentUser.value.organization);
const organizationActiveUsers = computed<AuthOrganizationUser[]>(() => currentOrganization.value.active_users);

const maxUsersToShow = 3;

const sortUsers = (initialUsers: AuthOrganizationUser[]): AuthOrganizationUser[] => {
  const users = [ currentUser.value as AuthOrganizationUser ] as AuthOrganizationUser[];
  initialUsers.forEach((user) => {
    if (user.id !== currentUser.value.id) {
      if (typeof user === 'object') {
        users.push(user);
      }
    }
  });
  return users;
};

const tooltipButtonUsers = computed<AuthOrganizationUser[]>(() => sortUsers(organizationActiveUsers.value));

const inviteButtonMoreUsers = computed<number>(() => (organizationActiveUsers.value.length > maxUsersToShow ? organizationActiveUsers.value.length - (maxUsersToShow - 1) : 0));

const inviteButtonUsers = computed<AuthOrganizationUser[]>(() => {
  const users = sortUsers(organizationActiveUsers.value);
  return users.slice(0, inviteButtonMoreUsers.value > 0 ? (maxUsersToShow - 1) : maxUsersToShow);
});

const { isMobileOrTablet, isTabletLarge } = storeToRefs(useScreensizeStore());

const isListDisplayed = computed<boolean>(() => !isMobileOrTablet.value || currentView.value === ViewType.LIST);
const isMapDisplayed = computed<boolean>(() => !isMobileOrTablet.value || currentView.value === ViewType.MAP);
const target = computed<LinkTarget>(() => (isMobileOrTablet.value ? '_self' : '_blank'));

const isImpersonated = computed<boolean>(() => UserAuth.isImpersonated());

const whatsappLink = computed<string>(() => {
  const phoneNumber = currentUser.value.phone_number;
  const text = trans('selection_item.cta.share.tooltip.whatsapp.message');

  return `https://wa.me/${phoneNumber}?text=${text}`;
});

const companyName = computed<string>(() => window.COMPANY_NAME);

const sendEmail = async (): Promise<void> => {
  await sendEmailWithLastProposal();
};

const getScrollValue = (position: number): number => {
  const banner = document.querySelector('#banner');
  const bannerHeight = banner ? banner.getBoundingClientRect().height : 0;
  const proposalHeaderHeight = isMobileOrTablet.value ? 0 : 64;

  return position
    - document.body.getBoundingClientRect().top
    - (document.querySelector('#header') as HTMLElement).getBoundingClientRect().height
    - bannerHeight
    - proposalHeaderHeight
    - 16;
};

const scrollToSelectionItem = async (id: number): Promise<void> => {
  if (isMobileOrTablet.value) {
    setView(ViewType.LIST);
    await nextTick();
  }

  const selectionItemElement = document.querySelector(`#selection-item-${id}`);

  if (!selectionItemElement) {
    return;
  }

  window.scrollTo({
    behavior: 'smooth',
    top: getScrollValue(selectionItemElement.getBoundingClientRect().top),
  });

  const scrollHighlightedClass = 'selection-item-element--scroll-highlighted';

  const scrollHighlightedProposalItem = document.querySelector(`.${scrollHighlightedClass}`);

  if (scrollHighlightedProposalItem) {
    scrollHighlightedProposalItem.classList.remove(scrollHighlightedClass);
  }

  selectionItemElement.classList.add(scrollHighlightedClass);

  setTimeout(() => {
    selectionItemElement.classList.remove(scrollHighlightedClass);
  }, 2000);
};

const initSortableSelectionItems = (): void => {
  if (!sortableSelectionItems.value) {
    return;
  }

  Sortable.create(sortableSelectionItems.value, {
    animation: 150,
    handle: '.selection-item-wrapper__drag-and-drop',
    filter: '.selection-item-wrapper__tooltip-panel',
    forceFallback: true,
    scroll: true,
    bubbleScroll: true,
    scrollSensitivity: 100,
    scrollSpeed: 15,
    onSort: async (event) => {
      const selectionItemIds: number[] = Array
        .from(event.to.children)
        .map((element) => Number((element as HTMLLIElement).dataset.id));

      await reorderItems(selectionItemIds);
    },
    onEnd: (event) => {
      const selectionItem = event.item.querySelector('.selection-item-element') as HTMLDivElement;
      selectionItem.classList.add('selection-item-element--dragged-highlighted');

      setTimeout(() => {
        selectionItem.classList.remove('selection-item-element--dragged-highlighted');
      }, 500);
    },
  });
};

// Backward compatibility for mails send before this ticket : https://bureaux-a-partager.atlassian.net/browse/MATCH-9577
const openSearchCommentFromProposal = (proposalId: number): void => {
  const proposalToOpen = (activeSelectionItems.value as SelectionItem[])
    .find((selectionItem) => selectionItem.proposal?.id === proposalId);

  if (proposalToOpen) {
    openCommentsModal(proposalToOpen);
  }
};

const openSearchCommentFromSelectionItem = (selectionItemId: number): void => {
  const selectionItemToOpen = (activeSelectionItems.value as SelectionItem[])
    .find((selectionItem) => selectionItem.id === selectionItemId);

  if (selectionItemToOpen) {
    openCommentsModal(selectionItemToOpen);
  }
};

onMounted(async () => {
  await nextTick();

  const bannerBoxShadow = document.querySelector('.banners-box-shadow');

  if (bannerBoxShadow) {
    bannerBoxShadow.remove();
  }

  initSortableSelectionItems();

  const urlSearchParams = new URLSearchParams(window.location.search);

  openSearchCommentFromProposal(Number(urlSearchParams.get('openProposalId')));

  openSearchCommentFromSelectionItem(Number(urlSearchParams.get('openSelectionItemId')));
});
</script>

<template>
  <div class="selection-item-app">
    <div
      :class="{ 'map-closed': !isMapDisplayed }"
      class="selection-item-content-section"
    >
      <div
        class="selection-item-content-section__title section-title"
        :class="{ impersonated: isImpersonated }"
      >
        <Typography
          as="h1"
          variant="content-heading-pay"
        >
          {{
            transchoice('selection_item.title', {
              count: currentOrganization.name.length,
              company_name: currentOrganization.name,
            })
          }}
        </Typography>

        <div class="section-title__ctas title-ctas">
          <TooltipClick
            v-if="isImpersonated"
            panel-class="invisible-cta__tooltip tooltip-menu"
          >
            <template #trigger>
              <Button class="title-ctas__invisible-cta invisible-cta" variant="invisible">
                <Icon
                  class="invisible-cta__icon"
                  :svg="IconUploadSimple"
                  width="24"
                  height="24"
                />
                <span
                  class="invisible-cta__text"
                  :class="{ hidden: isMobileOrTablet || isTabletLarge }"
                >
                  {{ trans('selection_item.cta.share') }}
                </span>
              </Button>
            </template>

            <template #panel="{ close }">
              <a
                class="btn-link tooltip-menu__entry"
                :href="whatsappLink"
                target="_blank"
                @click="close"
              >
                <Icon
                  :svg="IconWhatsapp"
                  fill="green-whatsapp"
                />
                <Typography variant="body">
                  {{ trans('selection_item.cta.share.tooltip.whatsapp') }}
                </Typography>
              </a>
              <div
                class="btn-link tooltip-menu__entry"
                @click="sendEmail"
                @click.stop="close"
              >
                <Icon
                  :svg="IconEnvelope"
                />
                <Typography variant="body">
                  {{ trans('selection_item.cta.share.tooltip.email') }}
                </Typography>
              </div>
            </template>
          </TooltipClick>

          <Tooltip :direction="Direction.DOWN">
            <template #trigger>
              <ButtonLink
                v-if="isListDisplayed"
                :id="SelectionItemProductTour.TOUR_INVITE_TEAM_MEMBER"
                class="title-ctas__cta title-cta"
                variant="white"
                :target="target"
                :href="teamLink"
              >
                <div class="title-cta__team cta-team">
                  <div
                    v-for="user in inviteButtonUsers"
                    :key="user.id"
                    class="cta-team__item cta-team__item--user"
                  >
                    <UserPicture
                      :user="user"
                      :size="32"
                    />
                  </div>
                  <div
                    v-if="inviteButtonMoreUsers > 0"
                    class="cta-team__item cta-team__item--counter"
                  >
                    <CounterPill
                      :count="inviteButtonMoreUsers"
                      :size="32"
                    />
                  </div>
                </div>
                <div class="title-cta__icon cta-icon">
                  <Icon :svg="IconUserPlus" :height="24" />
                </div>
                <div class="title-cta__text cta-text">
                  <Typography
                    variant="body-bold"
                    :class="{ hidden: isMobileOrTablet || isTabletLarge }"
                  >
                    {{ trans('selection_item.cta.add_team_member') }}
                  </Typography>
                </div>
              </ButtonLink>
            </template>

            <template #panel>
              <ul class="title-cta__tooltip">
                <li
                  v-for="user in tooltipButtonUsers"
                  :key="user.id"
                >
                  <Typography variant="body-small">
                    {{ user.full_name }}
                    <span v-if="user.id === currentUser.id">({{ trans('global.you').toLowerCase() }})</span>
                  </Typography>
                </li>
              </ul>
            </template>
          </Tooltip>
        </div>
      </div>

      <AccountManagerContactPanel
        v-if="accountManager"
        :account-manager="accountManager"
      />

      <StickyTabs
        v-show="isMobileOrTablet"
        :current-view="currentView"
        @set-view="setView"
      />

      <div
        v-show="isListDisplayed"
        class="selection-item-content-section__list"
        :class="{ 'impersonated': isImpersonated }"
      >
        <ul
          v-if="activeSelectionItems.length"
          ref="sortableSelectionItems"
          class="selection-item-active-list"
        >
          <li
            v-for="selectionItem in activeSelectionItems"
            :key="selectionItem.id"
            :data-id="selectionItem.id"
          >
            <SelectionItemWrapper :selection-item="selectionItem" />
          </li>
        </ul>

        <SelectionItemEmptyState v-else-if="removedSelectionItems.length">
          <template #description>
            <Typography variant="body" color="gray-darker">
              {{ trans('selection_item.finished_state.text', { company_name: companyName }) }}
            </Typography>
          </template>

          <template #actions>
            <ButtonLink
              :href="URL_SEARCH"
              variant="primary"
              class="selection-item__state-link selection-item__state-link--search"
            >
              {{ trans('selection_item.finished_state.button.search.text') }}
            </ButtonLink>
          </template>
        </SelectionItemEmptyState>

        <SelectionItemEmptyState v-else>
          <template #description>
            <Typography variant="body" color="gray-darker">
              {{ trans('selection_item.empty_state.text', { company_name: companyName }) }}
            </Typography>
          </template>

          <template #actions>
            <ButtonLink
              :href="URL_GUIDED_SEARCH"
              variant="primary"
              class="selection-item__state-link selection-item__state-link--guided-search"
            >
              {{ trans('selection_item.empty_state.button.guided_search.text') }}
            </ButtonLink>

            <ButtonLink
              :href="URL_SEARCH"
              variant="inverted"
              class="selection-item__state-link selection-item__state-link--search"
            >
              {{ trans('selection_item.empty_state.button.search.text') }}
            </ButtonLink>
          </template>
        </SelectionItemEmptyState>

        <div
          v-if="removedSelectionItems.length"
          class="selection-item-removed-section mt-10"
        >
          <Typography
            variant="content-heading-1"
            as="h3"
            class="selection-item-removed-title m-0"
          >
            {{ trans('selection_item.removed.title') }}
          </Typography>

          <ul class="selection-item-removed-list">
            <li
              v-for="selectionItem in removedSelectionItems"
              :key="selectionItem.id"
            >
              <SelectionItemElement :selection-item="selectionItem" />
            </li>
          </ul>
        </div>
      </div>
    </div>

    <SelectionItemMap
      v-show="isMapDisplayed"
      @marker-click="(id) => scrollToSelectionItem(id)"
    />

    <CommentsSlider />

    <CustomMarkerSlider />
  </div>
</template>

<style scoped lang="scss">
.selection-item {
  &__state-link {
    @apply w-auto;
  }
}
</style>
